import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { loadStandalonePage } from "~/utils/content-fns";
import { seo, seoLinks } from "~/utils/seo";
import { Button } from "~/components/Button";

export const Route = createFileRoute("/news/")({
  loader: async () => {
    const data = await loadStandalonePage({ data: { path: "news/_index.md" } });
    return data;
  },
  head: ({ loaderData }) => ({
    meta: seo({
      title: loaderData?.seo_title || "News",
      description: loaderData?.seo_description || "C1 news",
      url: "/news",
    }),
    links: seoLinks({ url: "/news" }),
  }),
  component: NewsIndex,
});

/* ------------------------------------------------------------------ */
/*  Shared SVGs                                                        */
/* ------------------------------------------------------------------ */
function PressReleaseIcon() {
  return (
    <svg className="relative h-6 w-auto" width="25" height="24" viewBox="0 0 25 24" fill="none">
      <rect x="0.666504" width="24" height="24" rx="3.78947" fill="#00343a" />
      <rect x="0.666504" width="24" height="24" rx="3.78947" fill="white" fillOpacity="0.5" />
      <path
        d="M7.56487 8.86315H6.31055V17.6434C6.31055 18.3333 6.87499 18.8978 7.56487 18.8978H16.3452V17.6434H7.56487V8.86315ZM17.5995 6.35449H10.0735C9.38365 6.35449 8.8192 6.91894 8.8192 7.60882V15.1348C8.8192 15.8247 9.38365 16.3891 10.0735 16.3891H17.5995C18.2894 16.3891 18.8538 15.8247 18.8538 15.1348V7.60882C18.8538 6.91894 18.2894 6.35449 17.5995 6.35449ZM17.5995 15.1348H10.0735V7.60882H17.5995V15.1348ZM11.3279 10.7446H16.3452V11.999H11.3279V10.7446ZM11.3279 12.6261H13.8365V13.8805H11.3279V12.6261ZM11.3279 8.86315H16.3452V10.1175H11.3279V8.86315Z"
        fill="#00343a"
      />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg className="h-auto w-4" width="16" height="17" viewBox="0 0 16 17" fill="none">
      <path d="M5.33337 1.72607V4.39274" stroke="#00343a" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.6666 1.72607V4.39274" stroke="#00343a" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12.6667 3.05933H3.33333C2.59695 3.05933 2 3.65628 2 4.39266V13.726C2 14.4624 2.59695 15.0593 3.33333 15.0593H12.6667C13.403 15.0593 14 14.4624 14 13.726V4.39266C14 3.65628 13.403 3.05933 12.6667 3.05933Z" stroke="#00343a" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2 7.05933H14" stroke="#00343a" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowRight() {
  return (
    <svg className="h-3 w-auto" width="13" height="12" viewBox="0 0 13 12" fill="none">
      <path fillRule="evenodd" clipRule="evenodd" d="M7.63454 1.06417L12.1267 5.55638C12.4024 5.83204 12.4024 6.27898 12.1267 6.55465L7.63454 11.0469C7.35888 11.3225 6.91194 11.3225 6.63627 11.0469C6.36061 10.7712 6.36061 10.3243 6.63627 10.0486L9.92346 6.76139L0.333496 6.76139L0.333496 5.34963L9.92346 5.34963L6.63627 2.06244C6.36061 1.78677 6.36061 1.33983 6.63627 1.06417C6.91194 0.788506 7.35888 0.788506 7.63454 1.06417Z" fill="currentColor" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/*  Load-more card grid                                                */
/* ------------------------------------------------------------------ */
function CardGrid({
  cards,
  renderCard,
  buttonSize = "lg",
}: {
  cards: any[];
  renderCard: (card: any, i: number) => React.ReactNode;
  buttonSize?: "lg" | "xl";
}) {
  const [visible, setVisible] = useState(6);
  const hasMore = visible < cards.length;

  return (
    <>
      <div className="mt-8 grid gap-6 md:grid-cols-3">
        {cards.slice(0, visible).map((card, i) => renderCard(card, i))}
      </div>
      {hasMore && (
        <div className="mt-10 flex justify-center md:mt-14 lg:mt-12">
          <Button size={buttonSize} variant="teal" onClick={() => setVisible((v) => v + 6)}>
            Load more
          </Button>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */
function NewsIndex() {
  const data = Route.useLoaderData() as any;

  return (
    <>
      {/* ============ Hero ============ */}
      <div className="relative overflow-hidden bg-[#161415] px-4 pb-12 pt-8 md:px-8 md:pt-12 lg:pt-16 xl:pb-28">
        <div className="mx-auto max-w-screen-xl">
          <div className="relative">
            <h1 className="font-display text-40 font-bold text-white md:text-48 xl:text-57">
              {data?.header?.heading || "News"}
            </h1>
            {data?.header?.copy && (
              <div className="mt-1 font-medium -tracking-1p text-white">
                {data.header.copy}
              </div>
            )}
            <a
              href="/news/feed.xml"
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-white/70 transition-colors hover:text-white"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6.18 15.64a2.18 2.18 0 1 1 0 4.36 2.18 2.18 0 0 1 0-4.36zM4 4.44A15.56 15.56 0 0 1 19.56 20h-2.83A12.73 12.73 0 0 0 4 7.27V4.44zm0 5.66a9.9 9.9 0 0 1 9.9 9.9h-2.83A7.07 7.07 0 0 0 4 12.93v-2.83z" />
              </svg>
              RSS Feed
            </a>
          </div>
          <hr className="mt-3 border-[#DCDADA]/30" />

          {/* Featured hero article */}
          {data?.hero && (
            <div className="relative mt-4 grid gap-6 overflow-hidden rounded-xl shadow-card lg:mt-8 lg:grid-cols-2 lg:gap-8 xl:mt-10 xl:items-center">
              <div className="block overflow-hidden lg:rounded-l-xl">
                {data.hero.image && (
                  <img
                    src={data.hero.image}
                    alt={data.hero.heading || ""}
                    className="rounded-10 rounded-14"
                  />
                )}
                {data.hero.credit && (
                  <div className="mt-2 text-xxs text-gray-1 lg:hidden">{data.hero.credit}</div>
                )}
              </div>
              <div>
                {data.hero.heading && (
                  <h2 className="font-display text-28 font-bold -tracking-1p text-white xl:text-33">
                    {data.hero.heading}
                  </h2>
                )}
                {data.hero.date && (
                  <time className="mt-3 inline-block text-13 text-white/70">
                    {new Date(data.hero.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </time>
                )}
                {data.hero.credit && (
                  <div className="mt-14 hidden text-xs text-gray-1 lg:block">{data.hero.credit}</div>
                )}
                {data.hero.url && (
                  <div className="mt-8">
                    <Button
                      href={data.hero.url}
                      size="md"
                      variant="teal"
                      {...(data.hero.url.startsWith("http") ? { target: "_blank", rel: "noopener" } : {})}
                    >
                      Learn more
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============ Featured Logos Strip ============ */}
      {data?.featured && (
        <div className="bg-[#161415] px-4 py-4 md:px-8 md:py-5">
          <div className="mx-auto max-w-sm md:max-w-screen-xl">
            {data.featured.heading && (
              <div className="text-center text-9 font-semibold uppercase tracking-14p text-white/70 md:text-11">
                {data.featured.heading}
              </div>
            )}
            {data.featured.images && (
              <div className="mt-4 flex flex-wrap justify-center gap-2 md:justify-between lg:gap-3">
                {(data.featured.images as string[]).map((src: string, i: number) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    className="h-5 w-auto lg:h-7 xl:h-8"
                    loading="lazy"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ Press Releases ============ */}
      {data?.press?.cards && (
        <div className="px-4 pt-12 md:px-8 lg:pt-16">
          <div className="mx-auto max-w-screen-xl">
            {data.press.heading && (
              <h2 className="font-display text-28 font-bold text-charcoal md:text-33 lg:text-40">
                {data.press.heading}
              </h2>
            )}
            <CardGrid
              cards={data.press.cards}
              buttonSize="lg"
              renderCard={(card: any, i: number) => (
                <div
                  key={i}
                  className="card-about group relative flex flex-col items-start rounded-2xl border border-[#DCDADA] bg-white p-5"
                >
                  <PressReleaseIcon />
                  <h3 className="relative mt-3 font-body font-bold leading-relaxed text-charcoal xl:text-19">
                    {card.heading}
                  </h3>
                  <div className="mt-5 flex w-full flex-1 flex-col justify-end gap-3 lg:mt-6 lg:flex-row lg:items-end lg:justify-between">
                    <div className="flex items-center gap-1.5">
                      <CalendarIcon />
                      {card.date && (
                        <time className="font-body text-13 font-medium text-gray-60">{card.date}</time>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 font-body text-13 font-medium text-gray-60 transition-all duration-300 group-hover:text-charcoal xl:text-base">
                      <span className="tracking-1p">Read more</span>
                      <ArrowRight />
                    </div>
                  </div>
                  <a
                    href={card.url}
                    title={card.heading}
                    className="absolute inset-0"
                    {...(card.url?.startsWith("http") ? { target: "_blank", rel: "noopener" } : {})}
                  />
                </div>
              )}
            />
            <hr className="mt-10 border-[#DCDADA] lg:mt-12" />
          </div>
        </div>
      )}

      {/* ============ News ============ */}
      {data?.news?.cards && (
        <div className="px-4 pb-16 pt-12 md:px-8 lg:py-16 xl:pb-20">
          <div className="mx-auto max-w-screen-xl">
            {data.news.heading && (
              <h2 className="font-display text-28 font-bold text-charcoal md:text-33 lg:text-40">
                {data.news.heading}
              </h2>
            )}
            <CardGrid
              cards={data.news.cards}
              buttonSize="xl"
              renderCard={(card: any, i: number) => (
                <div
                  key={i}
                  className="card-about group relative flex flex-col items-start overflow-hidden rounded-2xl border border-[#DCDADA] bg-white"
                >
                  {card.image && (
                    <img src={card.image} alt="" className="w-full" loading="lazy" />
                  )}
                  <h3 className="relative mx-5 mt-4 font-body font-bold leading-relaxed text-charcoal xl:text-19">
                    {card.heading}
                  </h3>
                  <div className="mt-5 flex w-full flex-1 flex-col justify-end gap-3 px-5 pb-5 lg:mt-6 lg:flex-row lg:items-end lg:justify-between">
                    <div className="flex items-center gap-1.5">
                      <CalendarIcon />
                      {card.date && (
                        <time className="font-body text-13 font-medium text-gray-60">{card.date}</time>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 font-body text-13 font-medium text-gray-60 transition-all duration-300 group-hover:text-charcoal xl:text-base">
                      <span className="tracking-1p">Read more</span>
                      <ArrowRight />
                    </div>
                  </div>
                  <a
                    href={card.url}
                    title={card.heading}
                    className="absolute inset-0"
                    {...(card.url?.startsWith("http") ? { target: "_blank", rel: "noopener" } : {})}
                  />
                </div>
              )}
            />
          </div>
        </div>
      )}

      {/* ============ Recognition ============ */}
      {data?.recognition?.cards && (
        <div className="px-4 pb-20 pt-12 md:px-8 lg:pb-24 lg:pt-16 xl:pb-20" style={{ background: "linear-gradient(180deg, #00343a 0%, #060606 100%)" }}>
          <div className="mx-auto max-w-screen-xl">
            {data.recognition.heading && (
              <h2 className="font-display text-28 font-bold text-white md:text-33 lg:text-40">
                {data.recognition.heading}
              </h2>
            )}
            <CardGrid
              cards={data.recognition.cards}
              buttonSize="xl"
              renderCard={(card: any, i: number) => (
                <div
                  key={i}
                  className="card-about group relative flex flex-col items-start overflow-hidden rounded-2xl bg-white"
                >
                  {card.image && (
                    <img src={card.image} alt="" className="w-full" loading="lazy" />
                  )}
                  <h3 className="relative mx-5 mt-4 font-body font-bold leading-relaxed text-charcoal xl:text-19">
                    {card.heading}
                  </h3>
                  <div className="mt-5 flex w-full flex-1 flex-col justify-end gap-3 px-5 pb-5 lg:mt-6 lg:flex-row lg:items-end lg:justify-between">
                    <div className="flex items-center gap-1.5 font-body text-13 font-medium text-gray-60 transition-all duration-300 group-hover:text-charcoal xl:text-base">
                      <span className="tracking-1p">Read more</span>
                      <ArrowRight />
                    </div>
                  </div>
                  <a
                    href={card.url}
                    title={card.heading}
                    className="absolute inset-0"
                    {...(card.url?.startsWith("http") ? { target: "_blank", rel: "noopener" } : {})}
                  />
                </div>
              )}
            />
          </div>
        </div>
      )}
    </>
  );
}
